import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import awsmobile from '../aws-exports';
import { get, ApiError } from 'aws-amplify/api';
import { Link } from 'react-router-dom';

import './EmployeesRank.css';

Amplify.configure(awsmobile);

// Score bands for the ranking. Deliberately named differently from the incident
// categories (Critical / Very Serious / ...) — a band describes an accumulated
// total, a category describes a single ticket, and reusing the names made the
// page ambiguous. Colours come from tokens and all meet WCAG AA.
const SCORE_BANDS = [
    { min: 25, label: 'Very high', color: 'var(--sev-critical)',     bg: 'var(--sev-critical-bg)' },
    { min: 20, label: 'High',      color: 'var(--sev-very-serious)', bg: 'var(--sev-very-serious-bg)' },
    { min: 15, label: 'Elevated',  color: 'var(--sev-severe)',       bg: 'var(--sev-severe-bg)' },
    { min: 10, label: 'Moderate',  color: 'var(--sev-moderate)',     bg: 'var(--sev-moderate-bg)' },
    { min: 0,  label: 'Low',       color: 'var(--sev-normal)',       bg: 'var(--sev-normal-bg)' },
];

const bandFor = (score) => SCORE_BANDS.find((b) => score >= b.min) || SCORE_BANDS[SCORE_BANDS.length - 1];

const EmployeesList = () => {

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        async function fetchEmployees() {
            try {
                // Notice how the rest operation's response is awaited
                const restOperation = get({
                    apiName: 'employees', // Replace 'employeesApi' with your actual API name
                    path: '/employees'
                });
                const { body } = await restOperation.response; // Waiting for the promise to resolve
                const jsonResponse = await body.json();
                // Publish the period so AccountabilityHeader can show it. The header mounts
                // before this request resolves, so it needs the event as well as the value.
                function setPeriodInLocalStorage(start_date, end_date) {
                    const period = JSON.stringify({ start_date, end_date });
                    localStorage.setItem('period', period);
                    window.dispatchEvent(new Event('periodUpdated'));
                }

                if (jsonResponse.period) {
                    setPeriodInLocalStorage(jsonResponse.period.start_date, jsonResponse.period.end_date);
                }
                setEmployees(jsonResponse.data); // Adjust according to actual response structure
                setLoading(false);
            } catch (e) {
                console.log('GET call failed:', e);
                if (e instanceof ApiError && e.response) {
                    // Parsing the error message from response
                    const errorResponse = JSON.parse(e.response.body);
                    console.error('Error response:', errorResponse);
                    setError(`Error: ${errorResponse.message}`);
                } else {
                    setError('Failed to fetch employees');
                }
                setLoading(false);
            }
        }

        fetchEmployees();
    }, []);

    // Bars are relative to the highest score on screen, so the ranking stays
    // readable whatever the absolute numbers are this period.
    const topScore = employees.reduce((max, e) => Math.max(max, Number(e.score) || 0), 0);

    const header = (
        <div className="page-header">
            {/* Was "Critical Performance Ranking", which read as the ranking of overall
                performance — so once the positive ranking existed alongside it, the two
                pages looked like a summary and a detail rather than two halves. Liability
                is the accounting pair for Asset: the names now explain each other, and
                Assets − Liabilities is visibly the figure they add up to. */}
            <h2 className="page-title">Liability Ranking</h2>
            <p className="page-subtitle">
                Total incident severity accumulated by each person this period.
                Higher points mean more severity — rank 1 needs the most attention.
            </p>
        </div>
    );

    if (loading) {
        return (
            <>
                {header}
                <p className="loading-state" role="status">Loading ranking…</p>
            </>
        );
    }

    if (error) {
        return (
            <>
                {header}
                <p className="error-state" role="alert">{error}</p>
            </>
        );
    }

    if (!employees.length) {
        return (
            <>
                {header}
                <p className="empty-state">
                    No scored incidents for this period yet. The daily ClickUp sync adds them as tickets are closed.
                </p>
            </>
        );
    }

    return (
        <div className="employees-container">
            {header}

            <div className="rank-legend">
                <span className="rank-legend-title">Score band</span>
                {SCORE_BANDS.map((band) => (
                    <span className="rank-legend-item" key={band.label}>
                        <span className="rank-legend-swatch" style={{ background: band.color }} />
                        {band.label}
                        <span style={{ color: 'var(--text-muted)' }}>
                            {band.min === 0 ? '(under 10)' : `(${band.min}+)`}
                        </span>
                    </span>
                ))}
            </div>

            <ul className="rank-list">
                {employees.map((employee, index) => {
                    const score = Number(employee.score) || 0;
                    const band = bandFor(score);
                    return (
                        <li key={employee.id}>
                            <Link to={`/employee/${employee.id}`} className="rank-row">
                                <div className="rank-row-top">
                                    <span className="rank-position">{index + 1}</span>
                                    <span className="rank-name">{employee.name}</span>
                                    <span className="rank-band" style={{ color: band.color, background: band.bg }}>
                                        {band.label}
                                    </span>
                                    <span className="rank-score">{score} pts</span>
                                </div>
                                <div className="rank-bar-track">
                                    <div
                                        className="rank-bar-fill"
                                        style={{
                                            width: topScore ? `${(score / topScore) * 100}%` : '0%',
                                            background: band.color,
                                        }}
                                    />
                                </div>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default EmployeesList;
